<template>
    <el-dialog title="字典管理" v-model="visible" width="90%" top="5vh" custom-class="dict-manager-dialog" @closed="onClosed">
        <el-alert title="新增项需要 App 支持才会生效；旧版 App 会忽略不认识的值" type="warning" :closable="false" show-icon
            class="dict-alert"></el-alert>
        <el-tabs v-model="type">
            <el-tab-pane v-for="t in types" :key="t.value" :label="t.label" :name="t.value"></el-tab-pane>
        </el-tabs>
        <div class="toolbar">
            <span class="toolbar-left">
                <el-radio-group v-if="type === 'led_status'" v-model="ledGroupFilter" size="mini">
                    <el-radio-button label="all">全部</el-radio-button>
                    <el-radio-button v-for="g in ledGroups" :key="g.value" :label="g.value">{{ g.label }}</el-radio-button>
                </el-radio-group>
                <span class="form-tip">{{ typeInfo.tip }}，拖动行调整顺序</span>
                <span v-if="type === 'led_status'" class="form-tip warn">
                    预设只是快捷模板，点「从预设添加」时整份复制到方案；修改预设不会影响已有方案
                </span>
            </span>
            <el-button type="primary" size="small" icon="el-icon-plus" @click="openForm()">新增</el-button>
        </div>
        <!-- 不同类型的列不同，切换类型时整表重建，拖拽事件随之重新绑定 -->
        <el-table ref="table" :key="type" :data="shownList" border size="small" row-key="id" v-loading="loading"
            :max-height="tableMaxHeight" :row-class-name="rowClass">
            <el-table-column width="40" align="center">
                <template #default>
                    <i class="el-icon-rank drag-handle"></i>
                </template>
            </el-table-column>
            <el-table-column v-if="type === 'default_image'" label="预览" width="80" align="center">
                <template v-slot="scope">
                    <el-image v-if="extraOf(scope.row).url" :src="extraOf(scope.row).url" fit="contain" class="image-thumb"
                        :preview-src-list="[extraOf(scope.row).url]"></el-image>
                </template>
            </el-table-column>
            <el-table-column v-if="type === 'led_status'" label="设备类型" width="110">
                <template v-slot="scope">{{ ledGroupLabel(extraOf(scope.row).group) }}</template>
            </el-table-column>
            <el-table-column v-if="type === 'led_effect'" label="预览" width="60" align="center">
                <template v-slot="scope">
                    <LedDot :colors="extraOf(scope.row).colors || []" :interval="extraOf(scope.row).interval || 0"></LedDot>
                </template>
            </el-table-column>
            <el-table-column :label="typeInfo.codeLabel" min-width="160">
                <template v-slot="scope">
                    <span v-if="type === 'led_status'" class="cell-flex">
                        <LedDot :colors="effectOf(scope.row.code).colors" :interval="effectOf(scope.row.code).interval"></LedDot>
                        <span v-if="scope.row.code" class="code wrap">{{ scope.row.code }}</span>
                        <span v-else class="form-tip">（空，灯灭）</span>
                    </span>
                    <span v-else-if="type === 'default_image'">
                        {{ imageTypeLabel(scope.row.code) }}
                        <span class="code">{{ scope.row.code }}</span>
                    </span>
                    <span v-else-if="scope.row.code" class="code wrap">{{ scope.row.code }}</span>
                    <span v-else class="form-tip">（空，灯灭）</span>
                </template>
            </el-table-column>
            <el-table-column v-if="type === 'led_status'" label="文案 key" min-width="200">
                <template v-slot="scope"><span class="code wrap">{{ extraOf(scope.row).key || '-' }}</span></template>
            </el-table-column>
            <el-table-column :label="typeInfo.nameLabel" min-width="180">
                <template v-slot="scope"><span class="wrap">{{ scope.row.name || '-' }}</span></template>
            </el-table-column>
            <template v-if="type === 'led_status'">
                <el-table-column label="标题" min-width="180">
                    <template v-slot="scope">
                        <span v-if="refSummary(extraOf(scope.row).title)" class="wrap">{{ refSummary(extraOf(scope.row).title) }}</span>
                        <span v-else class="form-tip">灯效动画名称</span>
                    </template>
                </el-table-column>
                <el-table-column label="说明" min-width="220">
                    <template v-slot="scope">
                        <span v-if="refSummary(ledContent(scope.row))" class="wrap">{{ refSummary(ledContent(scope.row)) }}</span>
                        <span v-else class="form-tip">App 本地翻译</span>
                    </template>
                </el-table-column>
                <el-table-column label="按钮" width="90" align="center">
                    <template v-slot="scope">
                        <span v-if="extraOf(scope.row).action">{{ actionLabel(extraOf(scope.row).action) }}</span>
                        <span v-else class="form-tip">无</span>
                    </template>
                </el-table-column>
            </template>
            <el-table-column v-if="type === 'default_image'" label="图片地址" min-width="260">
                <template v-slot="scope"><span class="code wrap">{{ extraOf(scope.row).url || '-' }}</span></template>
            </el-table-column>
            <el-table-column v-if="type === 'led_effect'" label="颜色 / 间隔" min-width="200">
                <template v-slot="scope">
                    <span class="code wrap">{{ (extraOf(scope.row).colors || []).filter(Boolean).join(' / ') || '-' }}</span>
                    <div class="form-tip">{{ extraOf(scope.row).interval ? `${extraOf(scope.row).interval} ms` : '常亮' }}</div>
                </template>
            </el-table-column>
            <el-table-column v-if="type === 'reset_color'" label="动画颜色" min-width="140">
                <template v-slot="scope">
                    <span v-if="extraOf(scope.row).color" class="cell-flex">
                        <LedDot :colors="[extraOf(scope.row).color]"></LedDot>
                        <span class="code">{{ extraOf(scope.row).color }}</span>
                    </span>
                    <span v-else class="form-tip">未配置</span>
                </template>
            </el-table-column>
            <el-table-column v-if="textType" :label="typeInfo.textLabel" min-width="200">
                <template v-slot="scope">
                    <span v-if="extraOf(scope.row).text" class="wrap">{{ textLabel(extraOf(scope.row).text) }}</span>
                    <span v-else class="form-tip">{{ scope.row.builtin ? 'App 本地翻译' : '未配置' }}</span>
                </template>
            </el-table-column>
            <el-table-column v-if="type === 'function'" label="类别" width="110" align="center">
                <template v-slot="scope">{{ functionTypeLabel(extraOf(scope.row).type) }}</template>
            </el-table-column>
            <el-table-column v-if="type === 'capability_field'" label="分组" width="140">
                <template v-slot="scope">{{ extraOf(scope.row).group || '-' }}</template>
            </el-table-column>
            <el-table-column v-if="type === 'capability_field'" label="App 用途说明" min-width="260">
                <template v-slot="scope"><span class="wrap">{{ extraOf(scope.row).usage || '-' }}</span></template>
            </el-table-column>
            <el-table-column v-if="type === 'text_type'" label="挂载位置" width="120" align="center">
                <template v-slot="scope">
                    <el-tag v-if="mountOf(scope.row)" size="mini">{{ mountLabel(mountOf(scope.row)) }}</el-tag>
                    <span v-else class="form-tip">固定字段</span>
                </template>
            </el-table-column>
            <el-table-column label="内置" width="70" align="center">
                <template v-slot="scope">
                    <el-tag v-if="scope.row.builtin" size="mini" type="info">内置</el-tag>
                </template>
            </el-table-column>
            <el-table-column label="引用数" prop="ref_count" width="70" align="center"></el-table-column>
            <el-table-column label="操作" width="110" align="center">
                <template v-slot="scope">
                    <el-button type="text" size="small" @click="openForm(scope.row)">编辑</el-button>
                    <el-tooltip v-if="deleteBlockedReason(scope.row)" :content="deleteBlockedReason(scope.row)" placement="top">
                        <span class="disabled-wrap">
                            <el-button type="text" size="small" disabled>删除</el-button>
                        </span>
                    </el-tooltip>
                    <el-button v-else type="text" size="small" style="color: #f56c6c;" @click="remove(scope.row)">删除</el-button>
                </template>
            </el-table-column>
        </el-table>

        <el-dialog :title="(form.id ? '编辑' : '新增') + formTypeInfo.label" v-model="formVisible" width="680px" append-to-body>
            <el-form ref="formRef" :key="formKey" :model="form" :rules="formRules" label-width="110px" size="small">
                <el-form-item v-if="form.type === 'led_status'" :label="formTypeInfo.codeLabel" prop="code">
                    <el-select v-model="form.code" :disabled="!!form.id" clearable filterable
                        placeholder="空，表示灯灭" style="width: 100%;">
                        <el-option v-for="e in effectOptions" :key="e.code" :label="`${e.name}（${e.code}）`" :value="e.code">
                            <span class="cell-flex">
                                <LedDot :colors="e.colors" :interval="e.interval"></LedDot>
                                <span>{{ e.name }}</span>
                                <span class="color-code">{{ e.code }}</span>
                            </span>
                        </el-option>
                    </el-select>
                    <div class="form-tip">从「灯效动画」中选择，创建后不能修改</div>
                </el-form-item>
                <el-form-item v-else-if="form.type === 'default_image'" :label="formTypeInfo.codeLabel" prop="code">
                    <el-select v-model="form.code" :disabled="!!form.id" placeholder="请选择图片类型" style="width: 100%;"
                        @change="onImageTypeChange">
                        <el-option v-for="t in imageTypeSelectOptions" :key="t.value" :label="`${t.label}（${t.value}）`"
                            :value="t.value"></el-option>
                    </el-select>
                    <div class="form-tip">每种图片类型一张，创建后不能修改</div>
                </el-form-item>
                <el-form-item v-else :label="formTypeInfo.codeLabel" prop="code">
                    <el-input v-model.trim="form.code" :disabled="!!form.id" :placeholder="formTypeInfo.codePlaceholder"></el-input>
                    <div class="form-tip">{{ form.id ? '创建后不能修改' : 'App 按它识别，创建后不能修改' }}</div>
                </el-form-item>
                <el-form-item v-if="form.type === 'led_status'" label="设备类型" prop="extra.group">
                    <el-select v-if="!form.id" v-model="form.extra.group" placeholder="请选择设备类型" style="width: 100%;">
                        <el-option v-for="g in ledGroups" :key="g.value" :label="g.label" :value="g.value"></el-option>
                    </el-select>
                    <span v-else>{{ ledGroupLabel(form.extra.group) }}</span>
                    <div class="form-tip">{{ form.id ? '创建后不能修改' : '方案从预设添加时按设备类型分组显示，创建后不能修改' }}</div>
                </el-form-item>
                <el-form-item v-if="form.type === 'led_status'" label="文案 key" prop="extra.key">
                    <el-input v-model.trim="form.extra.key" :disabled="!!form.id" placeholder="App 显示的灯效说明文案 key"></el-input>
                    <div class="form-tip">同一灯效 code 可对应不同的文案 key，创建后不能修改</div>
                </el-form-item>
                <el-form-item :label="formTypeInfo.nameLabel" prop="name">
                    <el-input v-model="form.name" :placeholder="formTypeInfo.namePlaceholder"></el-input>
                    <div v-if="formTypeInfo.nameTip" class="form-tip">{{ formTypeInfo.nameTip }}</div>
                </el-form-item>
                <template v-if="form.type === 'led_effect'">
                    <el-form-item label="颜色" prop="extra.colors.0">
                        <ColorInput v-model="form.extra.colors[0]"></ColorInput>
                        <div class="form-tip">闪烁色；常亮时为灯的颜色</div>
                    </el-form-item>
                    <el-form-item label="底色" prop="extra.colors.1">
                        <ColorInput v-model="form.extra.colors[1]" placeholder="可空，单色闪烁（等同 transparent）"></ColorInput>
                        <div class="form-tip">交替闪烁时的另一种颜色，常亮时不使用</div>
                    </el-form-item>
                    <el-form-item label="闪烁间隔" prop="extra.interval">
                        <el-input-number v-model="form.extra.interval" :min="0" :max="10000" :step="100"
                            controls-position="right"></el-input-number>
                        <span class="form-tip" style="margin-left: 8px;">毫秒，0 表示常亮</span>
                    </el-form-item>
                    <el-form-item label="预览">
                        <LedDot :colors="form.extra.colors" :interval="form.extra.interval || 0" :size="20"></LedDot>
                    </el-form-item>
                </template>
                <template v-if="form.type === 'led_status'">
                    <el-form-item label="标题" prop="extra.title">
                        <TextRefSelect v-model="form.extra.title" type="led_status" :texts="texts"
                            default-placeholder="不使用，App 显示灯效动画的名称"></TextRefSelect>
                        <div class="form-tip">从文案库选择（用途：灯效说明），为空时用灯效动画的名称</div>
                    </el-form-item>
                    <el-form-item label="灯效说明" prop="extra.content">
                        <LedContentField :content="form.extra.content" :actions="ledActions"
                            :action="form.extra.action" :texts="texts" :required="false"
                            @update:content="form.extra.content = $event"
                            @update:action="form.extra.action = $event"></LedContentField>
                        <div class="form-tip">文案从文案库选择（用途：灯效说明），说明支持富文本；同一方案下不同场景文案不同时可添加条件文案</div>
                        <div class="form-tip warn">这里只是快捷模板：保存后只影响以后「从预设添加」的新行，已有方案里的灯效说明不会变</div>
                    </el-form-item>
                </template>
                <template v-if="form.type === 'default_image'">
                    <el-form-item label="图片地址" prop="extra.url">
                        <el-input v-model.trim="form.extra.url" placeholder="https://..."></el-input>
                    </el-form-item>
                    <el-form-item v-if="form.extra.url" label="预览">
                        <el-image :src="form.extra.url" fit="contain" class="image-preview"
                            :preview-src-list="[form.extra.url]"></el-image>
                    </el-form-item>
                </template>
                <el-form-item v-if="form.type === 'reset_color'" label="动画颜色" prop="extra.color">
                    <ColorInput v-model="form.extra.color" placeholder="#RRGGBB 或颜色名，如 red"></ColorInput>
                    <div class="form-tip">App 没有该颜色的本地动画时，用这个颜色慢闪</div>
                </el-form-item>
                <el-form-item v-if="formTypeInfo.textType" :label="formTypeInfo.textLabel">
                    <el-select v-model="form.extra.text" clearable filterable placeholder="不使用"
                        style="width: 100%;">
                        <el-option v-for="t in textSelectOptions" :key="t.code" :label="t.label" :value="t.code"></el-option>
                    </el-select>
                    <div class="form-tip">{{ formTypeInfo.textTip }}</div>
                </el-form-item>
                <el-form-item v-if="form.type === 'function'" label="类别" prop="extra.type">
                    <el-radio-group v-model="form.extra.type" :disabled="!!form.id && form.builtin">
                        <el-radio v-for="f in functionTypes" :key="f.value" :label="f.value">{{ f.label }}</el-radio>
                    </el-radio-group>
                </el-form-item>
                <template v-if="form.type === 'capability_field'">
                    <el-form-item label="分组">
                        <el-input v-model.trim="form.extra.group" placeholder="如 录像、告警"></el-input>
                    </el-form-item>
                    <el-form-item label="App 用途说明">
                        <el-input v-model="form.extra.usage" type="textarea" :autosize="{ minRows: 2, maxRows: 6 }"
                            placeholder="App 用这个字段做什么"></el-input>
                    </el-form-item>
                </template>
                <el-form-item v-if="form.type === 'text_type'" label="挂载位置" prop="extra.mount">
                    <el-select v-if="!form.id" v-model="form.extra.mount" placeholder="请选择挂载位置" style="width: 100%;">
                        <el-option v-for="m in textMounts" :key="m.value" :label="m.label" :value="m.value"></el-option>
                    </el-select>
                    <span v-else>{{ form.extra.mount ? mountLabel(form.extra.mount) : '固定字段' }}</span>
                    <div class="form-tip">选择挂载位置后，对应对象的编辑页会多出这个文案项；App 新版本按 texts.&lt;code&gt; 读取，旧版本忽略。创建后不能修改</div>
                </el-form-item>
                <MountedTexts v-if="mountTypes.includes(form.type)" ref="mountedTexts" v-model="form.extra.texts" :mount="form.type"
                    :text-types="meta.text_types || []"></MountedTexts>
            </el-form>
            <template #footer>
                <el-button size="small" @click="formVisible = false">取消</el-button>
                <el-button type="primary" size="small" :loading="saving" @click="submit">保存</el-button>
            </template>
        </el-dialog>
    </el-dialog>
</template>

<script>
import pairingApi from './api'
import LedDot from './LedDot.vue'
import ColorInput, { COLOR_PATTERN } from './ColorInput.vue'
import LedContentField from './LedContentField.vue'
import TextRefSelect from './TextRefSelect.vue'
import MountedTexts from './MountedTexts.vue'
import {
    textOptionLabel, ledActionLabel, textRefSummary, validateTextRef, textTypeMount, cleanMountedTexts,
} from './utils'

// 可挂载文案的字典类型：表单里显示挂在该类型上的文案用途，存 extra.texts
const MOUNT_TYPES = ['function', 'led_effect', 'reset_color']
// meta 没有 text_mounts（后端未更新）时的兜底
const DEFAULT_TEXT_MOUNTS = [
    { value: 'scheme', label: '配网方案' },
    { value: 'category', label: '分类' },
    { value: 'function', label: '功能' },
    { value: 'led_effect', label: '灯效动画' },
    { value: 'reset_color', label: '复位颜色' },
]

const SETUP_SUCCESS = { value: 'setup_success', label: '配网成功引导图' }
const copyRef = (ref) => (ref && typeof ref === 'object' ? JSON.parse(JSON.stringify(ref)) : ref || '')

// textType：该字典项 extra.text 引用的文案用途；文案为空时 App 用本地翻译，新增项未配置时 App 显示名称/说明原文
const TYPES = [
    {
        value: 'function', label: '功能', tip: '配网方案可选的配网方式和设备特性', codeLabel: 'code', nameLabel: '名称', codePlaceholder: '如 support_ble', namePlaceholder: '如 蓝牙配网',
        textType: 'function', textLabel: '名称文案', textTip: '从文案库选择（用途：配网方式名称），App 显示的配网方式名称。为空时 App 用本地翻译',
    },
    { value: 'capability_field', label: '设备能力字段', tip: '设备能力配置中可填写的字段', codeLabel: 'code', nameLabel: '名称', codePlaceholder: '字段名', namePlaceholder: '字段说明' },
    {
        value: 'led_effect', label: '灯效动画', tip: '指示灯的颜色与闪烁方式，灯效预设和配网方案的灯效说明从这里选择', codeLabel: 'code', nameLabel: '名称',
        codePlaceholder: '如 red-slow', namePlaceholder: '如 红灯慢闪', nameTip: '后台显示用',
        textType: 'led_effect', textLabel: '灯效名称文案', textTip: '从文案库选择（用途：灯效名称），App 显示的灯效标题。为空时 App 用本地翻译，App 没有该灯效的翻译时显示「名称」',
    },
    {
        value: 'led_group', label: '灯效设备类型', tip: '灯效预设按设备类型分组，方便在对应机型下挑选预设；只影响后台分组，不下发 App',
        codeLabel: 'code', nameLabel: '名称', codePlaceholder: '如 normal', namePlaceholder: '如 普通设备',
        nameTip: '后台显示用',
    },
    {
        value: 'led_status', label: '灯效预设', tip: '配网方案添加灯效说明时可选的预设，按设备类型区分', codeLabel: '灯效 code', nameLabel: '备注', codePlaceholder: '可为空，表示灯灭', namePlaceholder: '如 红灯慢闪',
        // 标题用 TextRefSelect 编辑；说明、按钮用 LedContentField 编辑，文案用途都是 led_status
        refTextType: 'led_status',
    },
    {
        value: 'reset_color', label: '复位颜色', tip: '配网方案可选的复位指示灯颜色', codeLabel: 'code', nameLabel: '名称', codePlaceholder: '如 red', namePlaceholder: '如 红色',
        textType: 'reset_color', textLabel: '颜色名称文案', textTip: '从文案库选择（用途：颜色名称），App 复位引导里显示的颜色名。为空时 App 用本地翻译，App 没有该颜色的翻译时显示「红色」',
    },
    { value: 'image_type', label: '图片类型', tip: '配网方案可配置的图片类型', codeLabel: 'code', nameLabel: '名称', codePlaceholder: '如 main', namePlaceholder: '如 产品主图' },
    { value: 'default_image', label: '默认图片', tip: '配网方案没有对应类型的图片时 App 使用的默认图', codeLabel: '图片类型', nameLabel: '说明', namePlaceholder: '如 默认主图', nameTip: '后台显示用' },
    { value: 'text_type', label: '文案用途', tip: '文案库的用途分类，按挂载位置出现在配网方案、分类、功能等对象的编辑页中', codeLabel: 'code', nameLabel: '名称', codePlaceholder: '如 how_to_reset', namePlaceholder: '如 复位引导' },
    { value: 'language', label: '支持语言', tip: '文案库可填写的语言', codeLabel: '语言 code', nameLabel: '语言名', codePlaceholder: '如 en、pt', namePlaceholder: '如 English' },
]

const FUNCTION_TYPES = [
    { value: 'connection', label: '配网方式' },
    { value: 'feature', label: '设备特性' },
]

export default {
    name: 'PairingDictManager',
    components: { LedDot, ColorInput, LedContentField, TextRefSelect, MountedTexts },
    emits: ['changed'],
    data() {
        return {
            types: TYPES,
            functionTypes: FUNCTION_TYPES,
            mountTypes: MOUNT_TYPES,
            tableMaxHeight: 480,
            visible: false,
            loading: false,
            saving: false,
            changed: false,
            type: TYPES[0].value,
            list: [],
            drag: { from: -1, over: -1 },
            formVisible: false,
            form: { id: 0, type: '', code: '', name: '', extra: {} },
            // 灯效动画字典：灯效预设的 code 下拉与预览用
            effects: [],
            ledGroupFilter: 'all',
            // pairing/meta：设备类型、图片类型
            meta: {},
            formKey: '',
            formSeq: 0,
            // 当前类型引用的文案用途下的文案
            texts: [],
        }
    },
    computed: {
        // 灯效按钮字典（meta.led_actions），灯效说明的按钮下拉用
        ledActions() {
            return (this.meta && this.meta.led_actions) || []
        },
        typeInfo() {
            return TYPES.find((t) => t.value === this.type) || TYPES[0]
        },
        formTypeInfo() {
            return TYPES.find((t) => t.value === this.form.type) || this.typeInfo
        },
        textType() {
            return this.typeInfo.textType || ''
        },
        // 当前类型需要加载的文案用途：单选文案或标题/说明
        textsType() {
            return this.typeInfo.textType || this.typeInfo.refTextType || ''
        },
        ledGroups() {
            return this.meta.led_groups || []
        },
        textMounts() {
            return (this.meta.text_mounts && this.meta.text_mounts.length) ? this.meta.text_mounts : DEFAULT_TEXT_MOUNTS
        },
        imageTypeOptions() {
            const options = [...(this.meta.image_types || [])]
            if (!options.some((t) => t.value === SETUP_SUCCESS.value)) options.push(SETUP_SUCCESS)
            const current = this.form.type === 'default_image' && this.form.code
            if (current && !options.some((t) => t.value === current)) options.unshift({ value: current, label: current })
            return options
        },
        // 新增默认图片时排除已配置的类型（每种类型一张）
        imageTypeSelectOptions() {
            if (this.form.id) return this.imageTypeOptions
            const used = this.list.filter((d) => d.type === 'default_image').map((d) => d.code)
            return this.imageTypeOptions.filter((t) => !used.includes(t.value))
        },
        shownList() {
            if (this.type !== 'led_status' || this.ledGroupFilter === 'all') return this.list
            return this.list.filter((d) => this.extraOf(d).group === this.ledGroupFilter)
        },
        effectOptions() {
            return this.effects.map((e) => ({
                code: e.code,
                name: e.name || e.code,
                colors: (e.extra && e.extra.colors) || [],
                interval: (e.extra && e.extra.interval) || 0,
            }))
        },
        // 当前值不在文案库里时保留一项，避免下拉显示成空白
        textSelectOptions() {
            const options = this.texts.map((t) => ({ code: t.code, label: textOptionLabel(t) }))
            const current = this.form.extra && this.form.extra.text
            if (current && !options.some((o) => o.code === current)) {
                options.unshift({ code: current, label: `${current}（不在文案库中）` })
            }
            return options
        },
        formRules() {
            const rules = {
                name: [{ required: true, whitespace: true, message: `请输入${this.formTypeInfo.nameLabel}`, trigger: 'blur' }],
            }
            // 灯效 code 可为空（灯灭），文案 key 必填
            const refRule = { validator: (rule, value, callback) => {
                const err = validateTextRef(value)
                callback(err ? new Error(err) : undefined)
            }, trigger: 'change' }
            if (this.form.type === 'led_status') {
                rules['extra.key'] = [{ required: true, whitespace: true, message: '请输入文案 key', trigger: 'blur' }]
                rules['extra.group'] = [{ required: true, message: '请选择设备类型', trigger: 'change' }]
                rules['extra.title'] = [refRule]
                rules['extra.content'] = [refRule]
            } else if (this.form.type === 'default_image') {
                rules.code = [{ required: true, message: '请选择图片类型', trigger: 'change' }]
                rules['extra.url'] = [
                    { required: true, whitespace: true, message: '请输入图片地址', trigger: 'blur' },
                    { pattern: /^https?:\/\/\S+$/, message: '需为 http(s) 地址', trigger: 'blur' },
                ]
            } else {
                rules.code = [
                    { required: true, message: `请输入${this.formTypeInfo.codeLabel}`, trigger: 'blur' },
                    { pattern: /^\S+$/, message: '不能包含空格', trigger: 'blur' },
                ]
            }
            if (this.form.type === 'text_type' && !this.form.id) {
                rules['extra.mount'] = [{ required: true, message: '请选择挂载位置', trigger: 'change' }]
            }
            if (this.form.type === 'function') {
                rules['extra.type'] = [{ required: true, message: '请选择类别', trigger: 'change' }]
            }
            const colorRule = { pattern: COLOR_PATTERN, message: '需为 #RRGGBB、小写颜色名或 transparent', trigger: 'blur' }
            if (this.form.type === 'led_effect') {
                rules['extra.colors.0'] = [{ required: true, message: '请填写颜色', trigger: 'blur' }, colorRule]
                rules['extra.colors.1'] = [colorRule]
                rules['extra.interval'] = [{ required: true, type: 'number', min: 0, message: '请填写闪烁间隔', trigger: 'change' }]
            }
            if (this.form.type === 'reset_color') {
                rules['extra.color'] = [{ required: true, message: '请填写动画颜色', trigger: 'blur' }, colorRule]
            }
            return rules
        },
    },
    watch: {
        // 切换标签：重新加载该类型
        type() {
            if (!this.visible) return
            this.list = []
            this.ledGroupFilter = 'all'
            this.load()
        },
    },
    methods: {
        // type：打开时定位的字典类型，可选
        open(type) {
            this.changed = false
            // 弹窗高度约 90vh，减去标题、提示、标签页、工具栏的高度，表格超出时表头固定、内容滚动
            this.tableMaxHeight = Math.max(320, Math.floor(window.innerHeight * 0.9) - 300)
            this.visible = true
            this.loadMeta()
            // 类型有变化时由 watch 加载，避免重复请求
            if (type && type !== this.type && TYPES.some((t) => t.value === type)) {
                this.type = type
                return
            }
            this.load()
        },
        load() {
            const type = this.type
            this.loading = true
            pairingApi.DictList({ type }).then((res) => {
                // 切换标签过快时丢弃旧类型的结果
                if (type !== this.type) return
                this.loading = false
                this.list = res.data.data || []
                // 灯效动画本身也作为灯效预设的下拉来源，保存后同步
                if (type === 'led_effect') this.effects = this.list
                this.$nextTick(() => this.bindDrag())
            }).catch(() => {
                if (type === this.type) this.loading = false
            })
            if (type === 'led_status') this.loadEffects()
            if (this.textsType) this.loadTexts()
        },
        loadMeta() {
            pairingApi.PairingMeta().then((res) => {
                this.meta = res.data.data || {}
            }).catch(() => { })
        },
        loadEffects() {
            pairingApi.DictList({ type: 'led_effect' }).then((res) => {
                this.effects = res.data.data || []
            }).catch(() => { })
        },
        loadTexts() {
            const textType = this.textsType
            this.texts = []
            pairingApi.TextList({ type: textType }).then((res) => {
                if (textType === this.textsType) this.texts = res.data.data || []
            }).catch(() => { })
        },
        ledGroupLabel(value) {
            return (this.ledGroups.find((g) => g.value === value) || {}).label || value || '-'
        },
        imageTypeLabel(value) {
            return (this.imageTypeOptions.find((t) => t.value === value) || {}).label || value
        },
        mountOf(row) {
            return textTypeMount(this.extraOf(row))
        },
        mountLabel(value) {
            return (this.textMounts.find((m) => m.value === value) || {}).label || value
        },
        actionLabel(value) {
            return ledActionLabel(value)
        },
        // 旧数据的说明文案存在 extra.text
        ledContent(row) {
            const extra = this.extraOf(row)
            return extra.content || extra.text || ''
        },
        refSummary(ref) {
            return textRefSummary(ref, (code) => this.textLabel(code))
        },
        // 选择图片类型时，说明为空则用类型名
        onImageTypeChange(code) {
            if (!this.form.name) this.form.name = this.imageTypeLabel(code)
        },
        extraOf(row) {
            return row.extra || {}
        },
        effectOf(code) {
            const e = code && this.effectOptions.find((o) => o.code === code)
            return e || { colors: [], interval: 0 }
        },
        textLabel(code) {
            const t = this.texts.find((x) => x.code === code)
            return t ? textOptionLabel(t) : `${code}（不在文案库中）`
        },
        functionTypeLabel(value) {
            return (FUNCTION_TYPES.find((f) => f.value === value) || {}).label || value || '-'
        },
        deleteBlockedReason(row) {
            if (row.builtin) return '内置项不能删除'
            if (row.ref_count > 0) return `已被引用 ${row.ref_count} 次，不能删除`
            return ''
        },
        openForm(row) {
            const extra = { ...((row && row.extra) || {}) }
            if (this.type === 'function' && !extra.type) extra.type = 'connection'
            if (this.type === 'capability_field') {
                extra.group = extra.group || ''
                extra.usage = extra.usage || ''
            }
            if (this.type === 'led_status') {
                extra.key = extra.key || ''
                extra.group = extra.group || (row || this.ledGroupFilter === 'all' ? '' : this.ledGroupFilter)
                extra.title = copyRef(extra.title)
                extra.content = copyRef(extra.content || extra.text)
                extra.action = extra.action || ''
                delete extra.text
            }
            if (this.type === 'default_image') extra.url = extra.url || ''
            if (this.type === 'led_effect') {
                const colors = Array.isArray(extra.colors) ? extra.colors : []
                extra.colors = [colors[0] || '', colors[1] || '']
                extra.interval = typeof extra.interval === 'number' ? extra.interval : 500
            }
            if (this.type === 'reset_color') extra.color = extra.color || ''
            if (this.textType) extra.text = extra.text || ''
            if (this.type === 'text_type') {
                extra.mount = row ? textTypeMount(extra) : ''
                delete extra.guide
            }
            if (MOUNT_TYPES.includes(this.type)) extra.texts = JSON.parse(JSON.stringify(extra.texts || {}))
            // 表单记住自己的类型：弹窗关闭后切换页签，表单内容仍按打开时的类型渲染
            this.form = row
                ? { id: row.id, type: this.type, code: row.code || '', name: row.name || '', builtin: !!row.builtin, extra }
                : { id: 0, type: this.type, code: '', name: '', builtin: false, extra }
            // 不同类型、新增和编辑的表单项不同，每次打开都重建表单而不是在原表单上更新：
            // Vue 3.0.7 在 v-if 分支较多的表单上做增量更新时会报 parentNode 为 null
            this.formKey = `${this.type}-${++this.formSeq}`
            this.formVisible = true
            this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
        },
        submit() {
            this.$refs.formRef.validate((valid) => {
                if (!valid) return
                const textsErr = this.$refs.mountedTexts && this.$refs.mountedTexts.validate()
                if (textsErr) {
                    this.$message.error(textsErr)
                    return
                }
                const extra = { ...this.form.extra }
                if (this.form.type === 'capability_field') extra.usage = (extra.usage || '').trim()
                // 第二色为空表示单色闪烁，按约定存 transparent；常亮只用第一色
                if (this.form.type === 'led_effect') {
                    extra.colors = [extra.colors[0], extra.colors[1] || 'transparent']
                    extra.interval = extra.interval || 0
                }
                if (this.formTypeInfo.textType) extra.text = extra.text || ''
                if (MOUNT_TYPES.includes(this.form.type)) extra.texts = cleanMountedTexts(extra.texts)
                if (this.form.type === 'led_status') {
                    extra.title = extra.title || ''
                    extra.content = extra.content || ''
                    extra.action = extra.action || ''
                }
                const data = { type: this.form.type, code: this.form.code, name: this.form.name.trim(), extra }
                if (this.form.id) data.id = this.form.id
                this.saving = true
                pairingApi.DictSave(data).then((res) => {
                    this.saving = false
                    if (res.data.code == 200) {
                        this.$message.success('保存成功')
                        this.formVisible = false
                        this.changed = true
                        this.load()
                        // 文案用途变化后挂载文案项随之变化
                        if (data.type === 'text_type') this.loadMeta()
                        return
                    }
                    this.$message.error('保存失败' + (res.data.msg || ''))
                }).catch(() => {
                    this.saving = false
                })
            })
        },
        remove(row) {
            const label = row.name || row.code || this.extraOf(row).key
            this.$confirm(`确定删除「${label}」吗？`, '提示', { type: 'warning' }).then(() => {
                pairingApi.DictDelete({ id: row.id }).then((res) => {
                    if (res.data.code == 200) {
                        this.$message.success('删除成功')
                        this.changed = true
                        this.load()
                        return
                    }
                    this.$message.error(res.data.msg)
                })
            }).catch(() => { })
        },
        rowClass({ rowIndex }) {
            const { from, over } = this.drag
            if (from === rowIndex) return 'is-dragging'
            if (over === rowIndex && from !== rowIndex) return 'is-drag-over'
            return ''
        },
        // 与分类管理相同：按住手柄拖动，事件委托在 tbody 上
        bindDrag() {
            const tbody = this.$refs.table && this.$refs.table.$el.querySelector('.el-table__body-wrapper tbody')
            if (!tbody || tbody.__dragBound) return
            tbody.__dragBound = true
            const rowIndex = (e) => {
                const tr = e.target.closest && e.target.closest('tr')
                return tr ? Array.prototype.indexOf.call(tbody.children, tr) : -1
            }
            tbody.addEventListener('mousedown', (e) => {
                const tr = e.target.closest('tr')
                if (tr) tr.draggable = !!e.target.closest('.drag-handle')
            })
            tbody.addEventListener('dragstart', (e) => {
                this.drag = { from: rowIndex(e), over: -1 }
                e.dataTransfer.effectAllowed = 'move'
                e.dataTransfer.setData('text/plain', '')
            })
            tbody.addEventListener('dragover', (e) => {
                e.preventDefault()
                if (this.drag.from > -1) this.drag = { ...this.drag, over: rowIndex(e) }
            })
            tbody.addEventListener('drop', (e) => {
                e.preventDefault()
                const { from, over } = this.drag
                this.drag = { from: -1, over: -1 }
                if (from < 0 || over < 0 || from === over) return
                const shown = [...this.shownList]
                const [moved] = shown.splice(from, 1)
                shown.splice(over, 0, moved)
                // 筛选时只调整可见行之间的顺序，隐藏行位置不变
                const shownIds = new Set(shown.map((d) => d.id))
                let k = 0
                const next = this.list.map((d) => (shownIds.has(d.id) ? shown[k++] : d))
                this.list = next
                pairingApi.DictSort({ type: this.type, ids: next.map((d) => d.id) }).then((res) => {
                    if (res.data.code == 200) {
                        this.$message.success('排序已保存')
                        this.changed = true
                        return
                    }
                    this.$message.error('排序失败' + (res.data.msg || ''))
                    this.load()
                })
            })
            tbody.addEventListener('dragend', (e) => {
                this.drag = { from: -1, over: -1 }
                const tr = e.target.closest && e.target.closest('tr')
                if (tr) tr.draggable = false
            })
        },
        onClosed() {
            if (this.changed) this.$emit('changed')
        },
    },
}
</script>

<style scoped>
.dict-alert {
    margin-bottom: 4px;
}

.toolbar-left {
    display: inline-flex;
    align-items: center;
    gap: 10px;
}

.image-thumb {
    width: 40px;
    height: 40px;
    border: 1px solid #e4e7ed;
    border-radius: 3px;
}

.image-preview {
    width: 160px;
    height: 120px;
    border: 1px solid #e4e7ed;
    border-radius: 3px;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.form-tip {
    font-size: 12px;
    line-height: 18px;
    color: #909399;
}

.code {
    font-family: monospace;
    font-size: 12px;
}

.wrap {
    white-space: normal;
    word-break: break-all;
    line-height: 18px;
}

.cell-flex {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.color-code {
    margin-left: 12px;
    color: #909399;
    font-size: 12px;
}

.disabled-wrap {
    display: inline-block;
    margin-left: 10px;
}

.drag-handle {
    color: #c0c4cc;
    cursor: move;
}

::v-deep(.is-dragging) {
    opacity: 0.4;
}

::v-deep(.is-drag-over td) {
    border-top: 2px solid #409eff;
}

.form-tip.warn {
    color: #e6a23c;
}
</style>
